import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, InputBase, IconButton, Paper, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

interface PlayerResult {
  id: number;
  name: string;
  Position: string;
  MLB_AbbName: string | null;
}

/**
 * Standalone player search bar. On selection navigates to `urlPrefix/{id}`.
 * e.g. urlPrefix="/mlb-context" → /mlb-context/12345
 */
export default function PlayerSearchBar({
  prod,
  urlPrefix,
  placeholder = "Search for a player",
}: {
  prod: boolean;
  urlPrefix: string;
  placeholder?: string;
}) {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [matches, setMatches] = useState<PlayerResult[]>([]);
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Debounced fuzzy search
  useEffect(() => {
    if (!text.trim()) {
      setMatches([]);
      setOpen(false);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const controller = abortRef.current;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      const base = prod ? "https://oriolebird.pythonanywhere.com" : "";
      fetch(`${base}/search-fuzzy/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(text),
        signal: controller.signal,
      })
        .then((r) => r.json())
        .then((data: PlayerResult[]) => {
          if (Array.isArray(data)) {
            setMatches(data);
            setOpen(data.length > 0);
          }
        })
        .catch((err) => {
          if (err.name !== "AbortError") console.error(err);
        });
    }, 150);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [text, prod]);

  function handleSelect(player: PlayerResult) {
    setText("");
    setMatches([]);
    setOpen(false);
    navigate(`${urlPrefix}/${player.id}`);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && matches.length > 0) {
      handleSelect(matches[0]);
    }
  }

  return (
    <Box ref={containerRef} sx={{ position: "relative", width: 240 }}>
      <Paper
        variant="outlined"
        sx={{
          display: "flex",
          alignItems: "center",
          px: 1,
          py: 0.25,
          borderColor: "#293241",
          borderRadius: 1,
        }}
      >
        <SearchIcon sx={{ color: "text.secondary", fontSize: 18, mr: 0.5 }} />
        <InputBase
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          sx={{ flex: 1, fontSize: "0.85rem" }}
          inputProps={{ "aria-label": "player search" }}
        />
        {text && (
          <IconButton
            size="small"
            onClick={() => { setText(""); setMatches([]); setOpen(false); }}
            sx={{ p: 0.25 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Paper>

      {open && matches.length > 0 && (
        <Paper
          variant="outlined"
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1300,
            borderColor: "#293241",
            borderRadius: 1,
            mt: 0.25,
            overflow: "hidden",
          }}
        >
          {matches.map((player) => (
            <Box
              key={player.id}
              onClick={() => handleSelect(player)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 0.75,
                cursor: "pointer",
                "&:hover": { backgroundColor: "#e8f0fe" },
                borderBottom: "1px solid #f0f0f0",
                "&:last-child": { borderBottom: "none" },
              }}
            >
              <Typography variant="body2" fontWeight="bold" sx={{ flex: 1 }} noWrap>
                {player.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
              >
                {player.Position}
              </Typography>
              {player.MLB_AbbName && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    whiteSpace: "nowrap",
                    minWidth: 28,
                    textAlign: "right",
                  }}
                >
                  {player.MLB_AbbName}
                </Typography>
              )}
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
}
